<script lang="ts">
  /**
   * Leistungsabzeichen the flight would have met.
   *
   * Loud on purpose — a Gold height is the thing a pilot most wants to see when they open a
   * flight — and hedged just as deliberately, because **none of this can be claimed.** These
   * tracks come from network position reports and the IGC files carry no valid G-record, so the
   * page has to be enthusiastic and honest in the same breath. A card that only said
   * "Gold-Höhe!" would be the most misleading thing in the whole interface.
   *
   * Two visual states, and the difference is the whole point:
   *
   *   * **met** — the track settles it as far as a track can. Duration, and the height gain
   *     with its 100 m GPS malus already added.
   *   * **erflogen** — the performance is there and the condition cannot be established this
   *     way at all: a declaration in an approved recorder, or a barograph. Shown, because
   *     flying 520 km deserves to be on the page; shown differently, because it is not a claim.
   */
  import type { BadgeAssessment, BadgeLeg } from './api.ts';

  interface Props {
    assessment: BadgeAssessment;
  }

  const { assessment }: Props = $props();

  const MARK: Record<BadgeLeg['badge'], string> = {
    silber: '●',
    gold: '★',
    diamant: '◆',
    diplom: '✦',
  };

  const fmt = (l: BadgeLeg): string => {
    if (l.unit === 'h') return `${l.value.toFixed(1)} h`;
    if (l.unit === 'm') return `${Math.round(l.value)} m`;
    return `${l.value.toFixed(1)} km`;
  };
  const req = (l: BadgeLeg): string =>
    l.unit === 'h' ? `${l.required} h` : l.unit === 'm' ? `${l.required} m` : `${l.required} km`;
</script>

{#if assessment.achieved.length > 0}
  <section class="badges">
    <h2>Leistungsabzeichen</h2>

    <ul>
      {#each assessment.achieved as leg (leg.id)}
        <li class:met={leg.status === 'met'} class:indicative={leg.status === 'indicative'}>
          <span class="mark" aria-hidden="true">{MARK[leg.badge]}</span>
          <span class="what">
            <strong>{leg.label}</strong>
            <span class="numbers">{fmt(leg)} <span class="of">von {req(leg)}</span></span>
          </span>
          <span class="tag">{leg.status === 'met' ? 'erfüllt' : 'erflogen'}</span>
          {#if leg.caveat !== null}
            <span class="caveat">{leg.caveat}</span>
          {/if}
        </li>
      {/each}
    </ul>

    <p class="disclaimer">
      <strong>Kein Nachweis.</strong> Diese Datei stammt aus Netzwerk-Positionsmeldungen, hat
      keinen gültigen G-Record und wird von <code>vali-xxx</code> abgelehnt — für einen echten
      Antrag braucht es einen zugelassenen Flugschreiber. „Erfüllt“ heißt hier nur: der
      aufgezeichnete Flug hätte die Bedingung erreicht. Höhenbedingungen enthalten bereits den
      100-m-Malus für GPS-Höhe nach {assessment.rulesEdition} §2.4.3c; der Höhengewinn wird ab
      dem geschätzten Ausklinkpunkt gerechnet, nicht ab dem Platz.
    </p>
  </section>
{/if}

<style>
  .badges {
    border: 1px solid var(--line-strong);
    border-radius: 0.9rem;
    padding: 1rem 1.1rem;
    background: linear-gradient(160deg, rgba(247, 204, 74, 0.07), transparent 70%);
  }

  h2 {
    margin: 0 0 0.75rem;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--text-dim);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 0.15rem 0.75rem;
    padding: 0.6rem 0.75rem;
    border-radius: 0.6rem;
    background: var(--surface-2, #161a22);
  }

  li.met {
    /* The one that counts gets the accent; the other gets structure but not celebration. */
    box-shadow: inset 3px 0 0 var(--accent);
  }
  li.indicative {
    box-shadow: inset 3px 0 0 var(--line-strong);
  }

  .mark {
    font-size: 1.4rem;
    line-height: 1;
  }
  li.met .mark {
    color: var(--accent);
  }
  li.indicative .mark {
    color: var(--text-dim);
    opacity: 0.6;
  }

  .what {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }
  .what strong {
    font-size: 1rem;
  }
  .numbers {
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .of {
    opacity: 0.7;
  }

  .tag {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.2rem 0.5rem;
    border-radius: 999px;
    white-space: nowrap;
  }
  li.met .tag {
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 600;
  }
  li.indicative .tag {
    background: rgba(255, 255, 255, 0.06);
    color: var(--text-dim);
  }

  .caveat {
    grid-column: 2 / -1;
    font-size: 0.75rem;
    line-height: 1.45;
    color: var(--text-dim);
  }

  .disclaimer {
    margin: 0.85rem 0 0;
    font-size: 0.78rem;
    line-height: 1.55;
    color: var(--text-dim);
  }

  @media (max-width: 560px) {
    li {
      grid-template-columns: auto 1fr;
    }
    .tag {
      grid-column: 2;
      justify-self: start;
    }
  }
</style>
